import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip
} from "@mui/material";

import api from "../../api/api";

// Calendar
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";

import {
  format,
  parse,
  startOfWeek,
  getDay,
  addDays,
  isToday
} from "date-fns";

import enUS from "date-fns/locale/en-US";

const locales = {
  "en-US": enUS
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

export default function LeaveOverview() {

  const [todayLeaves, setTodayLeaves] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    loadOverview();
    loadCalendar();
    loadHolidays();
  }, []);

  // =====================================================
  // LEAVE TYPE COLOR MAPPING
  // =====================================================

  const leaveColors = {
    "Casual Leave": "#1976d2",
    "Sick Leave": "#d32f2f",
    "Earned Leave": "#2e7d32",
    "WFH": "#7b1fa2",
    "Leave": "#616161"
  };

  // ====================================
  // LOAD TODAY LEAVE OVERVIEW
  // ====================================

  const loadOverview = async () => {

    try {

      const res = await api.get("/leaves/overview");

      setTodayLeaves(res.data);

    } catch (error) {

      console.error("Error loading leave overview", error);

    }
  };

  // ====================================
  // LOAD HOLIDAYS
  // ====================================

  const loadHolidays = async () => {

    try {

      const res = await api.get("/holidays");

      setHolidays(res.data || []);

    } catch (error) {

      console.error("Error loading holidays", error);

    }
  };

  // ====================================
  // LOAD LEAVE EVENTS FOR CALENDAR
  // ====================================

  const loadCalendar = async () => {

    try {

      const res = await api.get("/leaves");

      const leaveEvents = res.data
        .filter((l) => l.status === "APPROVED")
        .map((leave) => {

          const leaveType = leave.leave_type || "Leave";

          const startDate = new Date(leave.start_date);
          const endDate = new Date(leave.end_date);

          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);

          return {

            title: `${leave.user_name || "User"} • ${leaveType}`,
            start: startDate,
            end: addDays(endDate, 1),
            allDay: true,
            leaveType: leaveType

          };

        });

      // MERGE HOLIDAYS
      const holidayEvents = holidays.map((h) => {

        const d = new Date(h.date);
        d.setHours(0,0,0,0);

        return {
          title: h.name,
          start: d,
          end: addDays(d,1),
          allDay: true
        };

      });

      setCalendarEvents([...leaveEvents, ...holidayEvents]);

    } catch (error) {

      console.error("Error loading calendar data", error);

    }
  };

  // ====================================
  // EVENT COLOR STYLING
  // ====================================

  const eventStyleGetter = (event) => {

    const backgroundColor =
      leaveColors[event.leaveType] || leaveColors["Leave"];

    const style = {
      backgroundColor,
      borderRadius: "6px",
      opacity: 0.9,
      color: "white",
      border: "0",
      display: "block",
      paddingLeft: "6px",
      fontSize: "13px"
    };

    return { style };
  };

  // ====================================
  // DAY CELL STYLING
  // ====================================

  const dayPropGetter = (date) => {

    let style = {};

    if (getDay(date) === 0) {
      style.backgroundColor = "#fafafa";
    }

    if (isToday(date)) {
      style.backgroundColor = "#e3f2fd";
      style.border = "2px solid #1976d2";
    }

    const holiday = holidays.find(
      (h) => new Date(h.date).toDateString() === date.toDateString()
    );

    if (holiday) {
      style.backgroundColor = "#fff3e0";
      style.border = "1px solid #ff9800";
    }

    return { style };
  };

  // ====================================
  // USER STATUS CHIP
  // ====================================

  const getStatus = (user) => {

    const leave = todayLeaves.find((l) => l.user_name === user.name);

    if (!leave) {
      return <Chip label="Working" color="success" size="small" />;
    }

    if (leave.half_day) {
      return <Chip label="Half Day" color="warning" size="small" />;
    }

    return <Chip label="On Leave" color="error" size="small" />;
  };

  return (

    <Box p={4}>

      <Typography variant="h5" mb={3}>
        Team Leave Status Today
      </Typography>

      <Grid container spacing={2} mb={5}>

        {todayLeaves.length === 0 && (
          <Typography color="text.secondary">
            No one is on leave today.
          </Typography>
        )}

        {todayLeaves.map((leave) => (

          <Grid item xs={12} md={4} key={leave.user_id}>

            <Card>

              <CardContent>

                <Typography variant="h6">
                  {leave.user_name}
                </Typography>

                <Box mt={1}>
                  {getStatus({ name: leave.user_name })}
                </Box>

                <Typography variant="body2" mt={1}>
                  {format(new Date(leave.start_date), "EEE, dd MMM yyyy")} →{" "}
                  {format(new Date(leave.end_date), "EEE, dd MMM yyyy")}
                </Typography>

              </CardContent>

            </Card>

          </Grid>

        ))}

      </Grid>

      <Typography variant="h5" mb={3}>
        Team Leave Calendar
      </Typography>

      <Card>

        <CardContent>

          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            eventPropGetter={eventStyleGetter}
            dayPropGetter={dayPropGetter}
            popup
            tooltipAccessor={(event) => event.title}
            style={{
              height: 650,
              background: "white",
              borderRadius: "10px",
              padding: "10px"
            }}
          />

        </CardContent>

      </Card>

    </Box>
  );
}